'use client';

import React from 'react';
import Link from 'next/link';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Calendar, ArrowUpRight } from 'lucide-react';
import CtaLabel, { CTA_BOOKING } from '@/components/ui/CtaLabel';
import { SECTION_HEADING_CLASS, SECTION_HEADING_STYLE } from './sectionHeading';

/* The terms that make the ask safe to accept. They were a wrapping inline row
   under the button, which broke onto a second line at most widths and read as
   a tagline. As a vertical list beside the button they read as what they are:
   the conditions of the offer. */
const TERMS = [
  'Free initial consultation',
  'No long-term contracts',
  // Was "Transparent pricing", which the site did not keep: there is no price
  // published anywhere on it. This says the thing that is actually true of how
  // the work is quoted.
  'Scope agreed before you commit',
  'Results-focused',
];

export default function FinalCTASection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    /* On the band rather than on white, so this and the footer read as one
       closing block. The page used to end on a sitemap; it now ends on an
       action, with the navigation underneath it rather than instead of it. */
    <section
      data-ground
      id="contact"
      className="section-rhythm m-band-top"
      ref={ref}
      style={{ background: 'var(--m-band)', color: 'var(--m-on-band)' }}
    >
      <div className="m-container">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8"
        >
          <div className="lg:col-span-7">
            <span
              className="m-eyebrow block mb-4"
              style={{ color: 'rgba(var(--m-on-band-rgb),0.6)' }}
            >
              Ready to grow?
            </span>
            <h2
              className={`${SECTION_HEADING_CLASS} mb-6`}
              style={{ ...SECTION_HEADING_STYLE, color: 'var(--m-on-band)' }}
            >
              Turn your next business challenge into a clear growth plan
            </h2>
            <p
              className="m-measure mb-10"
              style={{
                fontSize: 18,
                color: 'rgba(var(--m-on-band-rgb),0.72)',
                lineHeight: 1.62,
                maxWidth: 480,
              }}
            >
              Tell us what is not working. We will tell you what we would do about it, what it would
              take, and whether we are the right people to do it.
            </p>

            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <Link href="/book-a-consultation" className="m-btn m-btn--primary m-btn--on-band">
                <Calendar className="w-4 h-4" aria-hidden="true" />
                <CtaLabel mobile={CTA_BOOKING} desktop="Book a free consultation" />
              </Link>
              {/* Some visitors will never open a calendar widget. One line of
                  markup catches them rather than losing them. */}
              <a
                href="mailto:info@noventilabs.com"
                className="m-btn m-btn--tertiary m-wrap"
                style={{ color: 'rgba(var(--m-on-band-rgb),0.6)' }}
              >
                or email info@noventilabs.com
                <ArrowUpRight className="w-3.5 h-3.5 m-arrow" aria-hidden="true" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-5 lg:pl-8" style={{ borderLeft: '0' }}>
            <ul
              className="flex flex-col"
              style={{ borderTop: '1px solid rgba(var(--m-on-band-rgb),0.14)' }}
            >
              {TERMS.map((item) => (
                <li
                  key={`cta-term-${item}`}
                  className="flex items-center gap-3 py-4"
                  style={{ borderBottom: '1px solid rgba(var(--m-on-band-rgb),0.14)' }}
                >
                  <span
                    aria-hidden="true"
                    className="shrink-0"
                    style={{
                      width: 6,
                      height: 6,
                      background: 'var(--m-accent)',
                    }}
                  />
                  <span
                    className="m-eyebrow"
                    style={{ color: 'rgba(var(--m-on-band-rgb),0.72)', letterSpacing: '0.12em' }}
                  >
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
